import {
  ProductDetailColumns,
  ShipmentProductList,
  ShipmentProductLot,
} from '@/apis/types';
import { Flex, Table, Tooltip, Typography, message } from 'antd';
import React, { useMemo } from 'react';
import { CopyOutlinedRect } from '@/assets/icons/icons';
import { formatQuantity } from '@/utils/formatQuantity';

const { Text } = Typography;

interface Props {
  products: ShipmentProductList[];
  previewCount?: number | null; // only show first N rows if preview
  /** From the API (covers all products); computed from `products` if absent. */
  detailColumns?: ProductDetailColumns;
}

const SERIAL_PREVIEW_LIMIT = 4;

/** Fallback when the API gives no mode: serials win, then lot + expiry, else neither. */
export const getProductDetailColumns = (
  products: ShipmentProductList[]
): ProductDetailColumns => {
  if (products.some((p) => p.serial_preview?.length)) return 'serials';
  if (products.some((p) => p.lots?.length)) return 'lots';
  return null;
};

const ShipmentDetailProductsTable: React.FC<Props> = ({
  products,
  previewCount,
  detailColumns: detailColumnsProp,
}) => {
  const displayData = useMemo(() => {
    if (previewCount) return products.slice(0, previewCount);
    return products;
  }, [products, previewCount]);

  const getSerialPreview = (serials: string[]) => {
    if (!serials?.length) return '--';
    if (serials?.length <= SERIAL_PREVIEW_LIMIT) return serials?.join(', ');
    return `${serials.slice(0, SERIAL_PREVIEW_LIMIT).join(', ')} +${serials?.length - SERIAL_PREVIEW_LIMIT}`;
  };

  const handleCopySerials = async (serials: string[]) => {
    if (!serials?.length) return;
    await navigator.clipboard.writeText(serials.join(', '));
    message.success('Copied serials');
  };

  const detailColumns =
    detailColumnsProp === undefined
      ? getProductDetailColumns(products)
      : detailColumnsProp;

  const lotColumn = (
    title: string,
    key: keyof ShipmentProductLot,
    width: string
  ) => ({
    title,
    key,
    width,
    ellipsis: true,
    render: (_: unknown, record: ShipmentProductList) => {
      const lots = record?.lots ?? [];
      if (!lots.length) return <Text className='font-12'>--</Text>;
      return (
        <Flex vertical>
          {lots.map((lot) => (
            <Text
              key={`${lot.lot_number}|${lot.expiration_date}`}
              className='font-12 text-ellipsis'
              title={lot[key] || '--'}
            >
              {lot[key] || '--'}
            </Text>
          ))}
        </Flex>
      );
    },
  });

  const serialsColumn = {
    title: 'Serials',
    dataIndex: 'serial_preview',
    key: 'serial_preview',
    width: '40%',
    ellipsis: true,
    render: (_: string, record: ShipmentProductList) => {
      const serialArray = record?.serial_preview || [];

      return (
        <Flex justify='space-between' align='center'>
          <Text
            className='font-12 text-ellipsis'
            style={{
              maxWidth: 200,
            }}
            title={serialArray.join(', ')}
          >
            {getSerialPreview(serialArray)}
          </Text>
          <Tooltip title={!serialArray.length ? 'No serials' : 'Copy serials'}>
            <Text
              onClick={() =>
                handleCopySerials(serialArray).catch(() =>
                  message.error('Could not copy serials')
                )
              }
              style={{
                cursor: serialArray?.length ? 'pointer' : 'not-allowed',
                opacity: serialArray?.length ? 1 : 0.4,
              }}
            >
              <CopyOutlinedRect width={16} height={16} />
            </Text>
          </Tooltip>
        </Flex>
      );
    },
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '34%',
      ellipsis: true,
      render: (_: string, record: ShipmentProductList) => (
        <Flex vertical>
          <Text
            className='font-12'
            strong
            style={{
              maxWidth: 150,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={record?.name}
          >
            {record?.name}
          </Text>
          <Text
            className='font-12 text-ellipsis'
            style={{
              maxWidth: 150,
              color: 'var(--text-light)',
            }}
            title={record?.temperature_range}
          >
            {record?.temperature_range ? record?.temperature_range : '--'}
          </Text>
        </Flex>
      ),
    },
    {
      title: 'QTY',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 88,
      render: (val: number | null) => (
        <Text className='font-12'>
          {val !== null && val !== undefined ? formatQuantity(val) : val}
        </Text>
      ),
    },
    ...(detailColumns === 'serials' ? [serialsColumn] : []),
    ...(detailColumns === 'lots'
      ? [
          lotColumn('Lot #', 'lot_number', '28%'),
          lotColumn('Expiration date', 'expiration_date', '28%'),
        ]
      : []),
  ];

  return (
    <Table
      className='Shipment-details-product'
      rowKey='id'
      columns={columns}
      dataSource={displayData}
      pagination={false}
      rowHoverable={false}
      size='small'
    />
  );
};

export default ShipmentDetailProductsTable;
